import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '@/lib/use-auth';
import { type TutorProfileResponse } from '@/lib/schemas/tutor-profile';
import { useTutorProfile } from '@/lib/tutor-profile-context';
import {
  ONBOARDING_STEPS,
  ONBOARDING_STEP_KEYS,
  isOnboardingStepKey,
  nextStepKey,
  type OnboardingStepKey,
} from './steps';
import {
  IdentityStep,
  SubjectsStep,
  AvailabilityStep,
  BioStep,
  RateStep,
  PhotoStep,
  BankStep,
  ReviewStep,
} from './tutor-onboarding';

export function OnboardingPage() {
  const { user, accessToken } = useAuth();
  const navigate = useNavigate();
  const params = useParams<{ step?: string }>();
  const tutorCtx = useTutorProfile();

  const profile = tutorCtx?.profile ?? null;
  const loading = tutorCtx?.loading ?? true;

  const [saveError, setSaveError] = useState<string | null>(null);

  const requestedStep = params.step;
  const stepFromUrl: OnboardingStepKey | null =
    requestedStep && isOnboardingStepKey(requestedStep) ? requestedStep : null;

  const computedNextStep = useMemo<OnboardingStepKey>(
    () => nextStepKey(profile?.completedSteps ?? []),
    [profile?.completedSteps],
  );
  const activeStep: OnboardingStepKey = stepFromUrl ?? computedNextStep;

  useEffect(() => {
    if (loading) return;
    if (requestedStep !== activeStep) {
      navigate(`/onboarding/${activeStep}`, { replace: true });
    }
  }, [loading, requestedStep, activeStep, navigate]);

  // Clear save errors when the user navigates to a different step
  useEffect(() => {
    setSaveError(null);
  }, [activeStep]);

  if (
    !loading &&
    profile &&
    profile.publishStatus !== 'draft' &&
    profile.publishStatus !== 'changes_requested'
  ) {
    return <Navigate to="/" replace />;
  }

  if (!user || !accessToken) return null;

  const goToStep = (key: OnboardingStepKey) => navigate(`/onboarding/${key}`);

  const handleAfterSave = (
    next: TutorProfileResponse,
    advanceTo?: OnboardingStepKey,
    completedStep?: OnboardingStepKey,
  ) => {
    // Merge the union of what we already knew and what the server returned —
    // never let the server's array (which may lag a step) clobber local progress.
    const union = new Set<string>([
      ...(profile?.completedSteps ?? []),
      ...(next.completedSteps ?? []),
    ]);
    if (completedStep) union.add(completedStep);
    const merged: TutorProfileResponse = {
      ...next,
      completedSteps: ONBOARDING_STEP_KEYS.filter((k) => union.has(k)),
    };
    tutorCtx?.updateProfile(merged);
    setSaveError(null);
    if (advanceTo) goToStep(advanceTo);
  };

  const handleError = (err: unknown) => {
    if (err instanceof Error) {
      setSaveError(err.message);
    } else if (typeof err === 'string') {
      setSaveError(err);
    } else {
      setSaveError('Something went wrong — please retry.');
    }
  };

  return (
    <div className="mx-auto w-full max-w-4xl px-3 sm:px-4">
      {/* Changes-requested banner */}
      {profile?.publishStatus === 'changes_requested' && profile.changeRequestNote && (
        <div
          role="alert"
          className="mb-6 flex gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">Admin requested changes</p>
            <p className="mt-0.5">{profile.changeRequestNote}</p>
            <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
              Update your profile then re-submit on the Review step.
            </p>
          </div>
        </div>
      )}

      {/* Step card */}
      {loading ? (
        <div className="animate-pulse rounded-2xl border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div className="mb-4 h-5 w-48 rounded bg-slate-200 dark:bg-slate-700" />
          <div className="mb-6 h-3 w-72 rounded bg-slate-100 dark:bg-slate-700/60" />
          <div className="space-y-3">
            <div className="h-10 rounded-lg bg-slate-100 dark:bg-slate-700/60" />
            <div className="h-10 rounded-lg bg-slate-100 dark:bg-slate-700/60" />
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200/80 bg-white shadow-md dark:border-slate-700 dark:bg-slate-800">
          {/* Card header */}
          <div className="border-b border-slate-100 px-6 py-5 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-foreground">{titleFor(activeStep)}</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">{descriptionFor(activeStep)}</p>
          </div>

          {/* Card body */}
          <div className="px-4 py-6 sm:px-6">
            {saveError && (
              <div
                role="alert"
                className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-400"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {saveError}
              </div>
            )}

            {activeStep === 'identity' && (
              <IdentityStep
                profile={profile}
                accessToken={accessToken}
                firstName={user.firstName}
                lastName={user.lastName}
                onSaved={(p) => handleAfterSave(p, 'subjects', 'identity')}
                onError={handleError}
              />
            )}
            {activeStep === 'subjects' && (
              <SubjectsStep
                profile={profile}
                accessToken={accessToken}
                onSaved={(p) => handleAfterSave(p, 'availability', 'subjects')}
                onError={handleError}
              />
            )}
            {activeStep === 'availability' && (
              <AvailabilityStep
                profile={profile}
                accessToken={accessToken}
                onSaved={(p) => handleAfterSave(p, 'bio', 'availability')}
                onError={handleError}
              />
            )}
            {activeStep === 'bio' && (
              <BioStep
                profile={profile}
                accessToken={accessToken}
                onSaved={(p) => handleAfterSave(p, 'rate', 'bio')}
                onError={handleError}
              />
            )}
            {activeStep === 'rate' && (
              <RateStep
                profile={profile}
                accessToken={accessToken}
                onSaved={(p) => handleAfterSave(p, 'photo', 'rate')}
                onError={handleError}
              />
            )}
            {activeStep === 'photo' && (
              <PhotoStep
                profile={profile}
                accessToken={accessToken}
                onSaved={(p) => handleAfterSave(p, 'bank', 'photo')}
                onError={handleError}
              />
            )}
            {activeStep === 'bank' && (
              <BankStep
                profile={profile}
                accessToken={accessToken}
                onSaved={(p) => handleAfterSave(p, 'review', 'bank')}
                onError={handleError}
              />
            )}
            {activeStep === 'review' && (
              <ReviewStep
                profile={profile}
                accessToken={accessToken}
                onSaved={(p) => handleAfterSave(p, undefined, 'review')}
                onError={handleError}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function titleFor(step: OnboardingStepKey): string {
  return ONBOARDING_STEPS.find((s) => s.key === step)?.title ?? '';
}
function descriptionFor(step: OnboardingStepKey): string {
  return ONBOARDING_STEPS.find((s) => s.key === step)?.description ?? '';
}
